import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogIn, MessageSquare, Search, Globe } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useRef, useEffect } from "react";
import { T, getCurrentLang, supportedLanguages, translate, setLanguage } from "@/i18n";

const authPath = "/authz";

/**
 * App shell navigation for unauthenticated (or not-yet-hydrated) users.
 * Keeps logo, search, i18n, theme, and sign-in without the logged-in menu,
 * cart store, or creation wizard.
 */
const TopNavigationPublic = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const currentLang = getCurrentLang();

  useEffect(() => {
    if (location.pathname === "/search") {
      const params = new URLSearchParams(location.search);
      setSearchQuery(params.get("q") || "");
    } else {
      setSearchQuery("");
    }
  }, [location.pathname, location.search]);

  const isActive = (path: string) => location.pathname === path;

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode as any);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      searchInputRef.current?.blur();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setSearchQuery("");
      searchInputRef.current?.blur();
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 landscape:hidden lg:landscape:block">
      <div className="flex h-full items-center justify-between px-2">
        <Link to="/" className="flex items-center space-x-2">
          <img src="/iPhone.png" alt="Logo" className="h-8 w-auto object-contain" />
          <span className="font-bold text-lg hidden sm:inline-block">PolyMech</span>
        </Link>

        <div className="mx-4 max-w-md flex-1 hidden sm:block">
          <form onSubmit={handleSearchSubmit} className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="search"
              placeholder={translate("Search articles, pictures, files ...")}
              className="h-9 w-full border-0 bg-muted/50 pl-10 pr-4 focus-visible:ring-1 focus-visible:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </form>
        </div>

        <nav className="hidden items-center space-x-6 lg:flex" />

        <div className="flex items-center space-x-2">
          <Button
            variant={isActive("/support-chat") ? "default" : "ghost"}
            size="sm"
            onClick={() => navigate("/support-chat")}
            className="h-8 w-8 p-0"
            title={translate("Support Chat")}
          >
            <MessageSquare className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/search")}
            className="h-8 w-8 p-0 sm:hidden"
            title={translate("Search")}
          >
            <Search className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                title={translate("Language")}
              >
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {supportedLanguages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onSelect={() => handleLanguageChange(lang.code)}
                  className={currentLang === lang.code ? "bg-accent" : ""}
                >
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <ThemeToggle />

          <Button variant="ghost" size="sm" asChild>
            <Link to={authPath} className="flex items-center">
              <LogIn className="mr-2 h-4 w-4" />
              <T>Sign in</T>
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default TopNavigationPublic;
